import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { GlassCard } from "@/components/GlassCard";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { 
  LogOut, 
  Settings2, 
  Smartphone, 
  Cpu, 
  Mail, 
  Phone, 
  ShieldCheck, 
  RefreshCw,
  Lock,
  Unlock,
  CheckCircle2,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  Sparkles,
  Key,
  User,
  CreditCard,
  Layers
} from "lucide-react";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Active Tab: phone settings layout categories
  const [activeTab, setActiveTab] = useState("profile");

  const [settings, setSettings] = useState({
    whatsapp_enabled: false,
    email_enabled: true,
    push_enabled: false,
    phone_number: "+91 742 088 3406",
    contact_email: "",
    whatsapp_api_key: "",
    webhook_target_url: "",
    microcontroller_id: "",
    preferred_layout: "detailed_command",
    custom_provider: "openai",
    custom_model: "gpt-4o-mini",
    custom_api_key: "",
    // Advanced Credit Optimizer selections
    creator_app_model: "gpt-4o-mini",
    assistant_app_model: "gpt-4o-mini",
    copilot_app_model: "gpt-4o-mini"
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [pushStatus, setPushStatus] = useState("");

  // Stripe Payments states
  const [unlockedIntegrations, setUnlockedIntegrations] = useState([]);
  const [paymentPollingStatus, setPaymentPollingStatus] = useState("");
  const [paymentSessionId, setPaymentSessionId] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [activePlan, setActivePlan] = useState("Free Trial");

  // Parse URL parameters for Stripe callbacks
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    
    if (sessionId) {
      setPaymentSessionId(sessionId);
      pollPaymentStatus(sessionId);
    }
  }, [location]);

  const loadSettingsAndIntegrations = async () => {
    try {
      const [settingsRes, unlockedRes] = await Promise.all([
        api.get("/connectors/settings"),
        api.get("/payments/v1/checkout/unlocked")
      ]);
      
      const data = settingsRes.data;
      setSettings({
        ...data,
        contact_email: data.contact_email || user.email,
        creator_app_model: data.creator_app_model || "gpt-4o-mini",
        assistant_app_model: data.assistant_app_model || "gpt-4o-mini",
        copilot_app_model: data.copilot_app_model || "gpt-4o-mini"
      });
      
      const unlocked = unlockedRes.data || [];
      setUnlockedIntegrations(unlocked);
      
      if (unlocked.includes("enterprise_bulk_subscription")) {
        setActivePlan("Enterprise Pro Bundle");
      } else {
        setActivePlan("Free Trial");
      }
    } catch (e) {
      console.error("Failed to load user settings or integrations", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadSettingsAndIntegrations();
    }
  }, [user]);

  // Poll checkout status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    setPaymentPollingStatus("pending");
    
    try {
      const res = await api.get(`/payments/v1/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPaymentPollingStatus("success");
        loadSettingsAndIntegrations();
        setTimeout(() => {
          setPaymentPollingStatus("");
          navigate("/profile", { replace: true });
        }, 4000);
      } else if (res.data.status === "expired") {
        setPaymentPollingStatus("expired");
      } else {
        if (attempts < maxAttempts) {
          setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        } else {
          setPaymentPollingStatus("timeout");
        }
      }
    } catch (e) {
      console.error("Stripe polling error", e);
      setPaymentPollingStatus("error");
    }
  };

  // Launch Stripe Checkout Session for Bulk Suite Subscription
  const handleInitiateBulkSubscription = async () => {
    setSubscribing(true);
    try {
      const payload = {
        integration_id: "enterprise_bulk_subscription",
        origin_url: window.location.origin
      };
      
      const res = await api.post("/payments/v1/checkout/session", payload);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error("Initiate subscription failed", e);
      alert("Checkout error: Please ensure Stripe environment keys are active.");
    } finally {
      setSubscribing(false);
    }
  };

  // Unsubscribe / Cancel Plan directly in Settings
  const handleUnsubscribe = async () => {
    if (!window.confirm("Are you sure you want to cancel your Enterprise Pro Bundle subscription? All integrations will revert to locked status.")) return;
    setSaving(true);
    try {
      // Clear premium subscription from unlocked set
      await api.put("/connectors/settings", {
        ...settings,
        unlocked_integrations: []
      });
      
      // Update local payments record
      await api.get("/payments/v1/checkout/unlocked"); // trigger recalculation
      
      // Trigger a clean settings reload
      loadSettingsAndIntegrations();
      alert("Subscription cancelled successfully. Your plan has been set to Free Trial.");
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveSuccess(false);
    try {
      await api.put("/connectors/settings", settings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  // Simulate Web Push registration
  const registerWebPushNotifications = () => {
    setPushStatus("requesting");
    setTimeout(() => {
      api.post("/connectors/push/subscribe", {
        endpoint: "https://fcm.googleapis.com/fcm/send/sopflow-push-id-992",
        keys: {
          auth: "VAPID-PUBLIC-SOPFLOW-RSA",
          p256dh: "P256-CLIENT-BROWSER-KEY"
        }
      }).then(() => {
        setPushStatus("success");
        setSettings(prev => ({ ...prev, push_enabled: true }));
      }).catch(() => {
        setPushStatus("failed");
      });
    }, 1000);
  };

  const isSuiteActive = unlockedIntegrations.includes("enterprise_bulk_subscription");

  // Phone style Settings category list items
  const settingCategories = [
    { id: "profile", name: "Profile & Account", desc: "User avatars, active session logs", icon: User },
    { id: "subscription", name: "Plan & Subscriptions", desc: "Change plans, billing logs, Stripe portal", icon: CreditCard },
    { id: "connectors", name: "Connectors & Dispatches", desc: "WhatsApp, SMTP mail, push toggles", icon: Settings2 },
    { id: "ai_models", name: "AI Models & Credit Router", desc: "Grouped application credit optimizer", icon: Key }
  ];

  return (
    <div className="space-y-6 pb-12" data-testid="profile-page">
      <header>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-white">System Settings</h1>
        <p className="text-[#9AA4B2] text-sm mt-1">Manage your professional credentials, change pricing plans, configure device buses, and optimize AI models.</p>
      </header>

      {/* Stripe payment status notification overlay */}
      {paymentPollingStatus && (
        <GlassCard className={`p-4 border ${
          paymentPollingStatus === "pending" ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-300" :
          paymentPollingStatus === "success" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 animate-pulse" :
          "border-rose-500/30 bg-rose-500/10 text-rose-300"
        }`}>
          <div className="flex items-center gap-3">
            {paymentPollingStatus === "pending" && <RefreshCw className="animate-spin text-cyan-300" size={18} />}
            {paymentPollingStatus === "success" && <CheckCircle2 className="text-emerald-400" size={18} />}
            {(paymentPollingStatus === "expired" || paymentPollingStatus === "timeout" || paymentPollingStatus === "error") && <AlertTriangle className="text-rose-400" size={18} />}
            <div>
              <div className="font-semibold text-white text-sm">
                {paymentPollingStatus === "pending" && "Verifying Subscription Payment with Stripe Secure Relay..."}
                {paymentPollingStatus === "success" && "Subscription Activated! All Connectors are Unlocked."}
                {paymentPollingStatus === "expired" && "Checkout session expired. Please attempt again."}
                {paymentPollingStatus === "timeout" && "Payment verification timed out. Please refresh page."}
                {paymentPollingStatus === "error" && "Verification failed. Check Stripe provider keys."}
              </div>
              <p className="text-xs mt-0.5 opacity-90">
                {paymentPollingStatus === "pending" && "Decrypting transaction seal hashes to update organization settings..."}
                {paymentPollingStatus === "success" && "Database successfully authorized and notification logs dispatched."}
              </p>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Phone-style Settings Layout Grid */}
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        
        {/* Left Side: Phone style Category List items */}
        <div className="space-y-2">
          {settingCategories.map((cat) => {
            const IsActive = activeTab === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveTab(cat.id)}
                className={`w-full text-left p-3.5 rounded-2xl border transition flex items-center gap-3.5 ${
                  IsActive 
                    ? "bg-white/[0.06] text-white border-white/10 shadow-lg" 
                    : "bg-white/[0.01] border-transparent text-[#9AA4B2] hover:text-white hover:bg-white/[0.03]"
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  IsActive ? "bg-gradient-to-br from-[#2563EB]/40 to-[#4F46E5]/40 text-white" : "bg-white/[0.04] text-[#5B6472]"
                }`}>
                  <cat.icon size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{cat.name}</div>
                  <div className="text-[10px] text-[#5B6472] truncate">{cat.desc}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Right Side: Active Category Form panel */}
        <div className="space-y-6">
          
          {/* TAB 1: PROFILE & ACCOUNT */}
          {activeTab === "profile" && (
            <GlassCard className="p-6 space-y-6">
              <div className="flex items-center gap-2 border-b border-white/5 pb-3">
                <User size={18} className="text-indigo-400" />
                <h2 className="font-display text-xl text-white">Profile & Account</h2>
              </div>
              
              <div className="flex items-center gap-5 p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                <div className="w-16 h-16 rounded-3xl overflow-hidden bg-gradient-to-br from-indigo-500/60 to-cyan-500/60 flex items-center justify-center border border-white/10 shadow-[0_0_28px_-6px_rgba(79,70,229,0.7)]">
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white text-xl font-display font-semibold">
                      {(user.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2)}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-display text-2xl text-white">{user.name}</div>
                  <div className="text-sm text-[#9AA4B2]">{user.email}</div>
                  <div className="mt-2 flex gap-2 flex-wrap text-[11px]">
                    <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 capitalize">{user.role}</span>
                    {user.department && <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10">{user.department}</span>}
                    {user.location && <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10">{user.location}</span>}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-widest text-[#5B6472] font-semibold mb-3">Active Session details</h3>
                <div className="space-y-2 text-xs text-[#9AA4B2]">
                  <div className="flex justify-between p-2 rounded-lg bg-white/[0.01]">
                    <span>Authentication Method</span>
                    <span className="text-white">JWT Secure httpOnly Cookie</span>
                  </div>
                  <div className="flex justify-between p-2 rounded-lg bg-white/[0.01]">
                    <span>Current Access Token</span>
                    <span className="text-white font-mono">sopflow_jwt_active</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-white/5">
                <Button
                  onClick={async () => {
                    await logout();
                    navigate("/signin");
                  }}
                  className="btn-ghost-glass rounded-full px-5 py-2.5"
                  data-testid="profile-logout-btn"
                >
                  <LogOut size={14} className="mr-1.5" /> Sign out of all devices
                </Button>
              </div>
            </GlassCard>
          )}

          {/* TAB 2: PLAN & SUBSCRIPTIONS */}
          {activeTab === "subscription" && (
            <GlassCard className="p-6 space-y-6">
              <div className="flex items-center gap-2 border-b border-white/5 pb-3">
                <CreditCard size={18} className="text-cyan-400" />
                <h2 className="font-display text-xl text-white">Plan & Subscriptions</h2>
              </div>

              <div className="p-5 rounded-2xl border border-cyan-500/20 bg-cyan-500/5 flex flex-col sm:flex-row sm:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl" />
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs text-cyan-300 mb-2">
                    <Sparkles size={11} /> Enterprise Active Plan
                  </div>
                  <h3 className="text-lg font-bold text-white">SOPFlow Pro Suite</h3>
                  <p className="text-xs text-[#9AA4B2] mt-0.5">Enables unlimited WhatsApp, ESP32 microcontrollers, and SAP webhooks.</p>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#5B6472]">Active Plan Charge</div>
                  <div className="text-2xl font-bold text-white mt-0.5">$29.00<span className="text-xs font-normal text-[#9AA4B2]">/mo</span></div>
                </div>
              </div>

              {/* Stripe Subscription Actions: Subscribe vs Cancel */}
              <div className="space-y-4">
                <h3 className="text-xs uppercase tracking-widest text-[#5B6472] font-semibold">Change Pricing Plan</h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Standard Package */}
                  <div className="p-4 rounded-2xl border border-white/5 bg-white/[0.01] flex flex-col justify-between h-36">
                    <div>
                      <h4 className="text-sm font-semibold text-white">Standard Bundle</h4>
                      <p className="text-[10px] text-[#9AA4B2] mt-0.5">Includes unlimited emails and PWA push notifications.</p>
                    </div>
                    <div className="flex justify-between items-end">
                      <span className="text-base font-bold text-white">$15.00/mo</span>
                      <Button
                        type="button"
                        disabled={!isSuiteActive}
                        onClick={() => alert("Standard Plan is currently pre-packaged inside your active Pro Bundle!")}
                        className="btn-ghost-glass text-[10px] rounded-full px-3 py-1.5"
                      >
                        Included
                      </Button>
                    </div>
                  </div>

                  {/* Pro Bundle Package */}
                  <div className={`p-4 rounded-2xl border flex flex-col justify-between h-36 relative overflow-hidden ${
                    isSuiteActive ? "bg-cyan-500/5 border-cyan-500/30 text-cyan-300" : "bg-white/[0.01] border-white/5"
                  }`}>
                    {isSuiteActive && (
                      <div className="absolute top-2 right-2 text-[8px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded px-1.5 py-0.5">
                        ACTIVE
                      </div>
                    )}
                    <div>
                      <h4 className="text-sm font-semibold text-white">Enterprise Pro Bundle</h4>
                      <p className="text-[10px] text-[#9AA4B2] mt-0.5">Unlocks all physical ESP32 and enterprise SAP triggers in-bulk.</p>
                    </div>
                    <div className="flex justify-between items-end">
                      <span className="text-base font-bold text-white">$29.00/mo</span>
                      {!isSuiteActive ? (
                        <Button
                          type="button"
                          onClick={handleInitiateBulkSubscription}
                          disabled={subscribing}
                          className="btn-primary-glow text-[10px] rounded-full px-4 py-1.5"
                        >
                          {subscribing ? "Opening Stripe..." : "Subscribe"}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleUnsubscribe}
                          className="bg-rose-500/10 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 text-[10px] rounded-full px-4 py-1.5 font-bold transition"
                        >
                          Unsubscribe
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </GlassCard>
          )}

          {/* TAB 3: CONNECTORS & DISPATCHES */}
          {activeTab === "connectors" && (
            <GlassCard className="p-6 space-y-6">
              <div className="flex items-center gap-2 border-b border-white/5 pb-3">
                <Settings2 size={18} className="text-indigo-400" />
                <h2 className="font-display text-xl text-white">Connectors & Dispatches</h2>
              </div>

              {loading ? (
                <div className="text-xs text-[#9AA4B2]">Loading parameters...</div>
              ) : (
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-[#5B6472] font-semibold mb-3">Active Dispatch Channels</h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {/* Email */}
                      <div className="p-3.5 rounded-2xl border bg-white/[0.01] border-white/5 flex items-center justify-between">
                        <div>
                          <div className="text-xs font-semibold text-white flex items-center gap-1.5">
                            <Mail size={14} className="text-indigo-400" />
                            Email
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggle("email_enabled")}
                          className="text-[#9AA4B2] hover:text-white transition"
                        >
                          {settings.email_enabled ? <ToggleRight size={28} className="text-indigo-400" /> : <ToggleLeft size={28} />}
                        </button>
                      </div>

                      {/* WhatsApp (requires subscription) */}
                      <div className={`p-3.5 rounded-2xl border flex items-center justify-between relative overflow-hidden ${
                        !isSuiteActive ? "opacity-50 border-dashed border-white/5" : "bg-white/[0.01] border-white/5"
                      }`}>
                        {!isSuiteActive && (
                          <div className="absolute top-1.5 right-1.5 text-[7px] bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 rounded px-1.5 py-0.5">
                            🔒
                          </div>
                        )}
                        <div>
                          <div className="text-xs font-semibold text-white flex items-center gap-1.5">
                            <Phone size={14} className="text-emerald-400" />
                            WhatsApp
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={!isSuiteActive}
                          onClick={() => handleToggle("whatsapp_enabled")}
                          className="text-[#9AA4B2] hover:text-white transition disabled:cursor-not-allowed"
                        >
                          {!isSuiteActive ? <Lock size={14} className="text-[#5B6472]" /> : settings.whatsapp_enabled ? <ToggleRight size={28} className="text-emerald-400" /> : <ToggleLeft size={28} />}
                        </button>
                      </div>

                      {/* Web Push */}
                      <div className="p-3.5 rounded-2xl border bg-white/[0.01] border-white/5 flex items-center justify-between">
                        <div>
                          <div className="text-xs font-semibold text-white flex items-center gap-1.5">
                            <Smartphone size={14} className="text-cyan-400" />
                            Chrome push
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggle("push_enabled")}
                          className="text-[#9AA4B2] hover:text-white transition"
                        >
                          {settings.push_enabled ? <ToggleRight size={28} className="text-cyan-400" /> : <ToggleLeft size={28} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">Personal WhatsApp Number</label>
                      <input
                        type="text"
                        value={settings.phone_number || ""}
                        onChange={(e) => handleInputChange("phone_number", e.target.value)}
                        className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-indigo-400 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">Contact Email address</label>
                      <input
                        type="email"
                        value={settings.contact_email || ""}
                        onChange={(e) => handleInputChange("contact_email", e.target.value)}
                        className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-indigo-400 transition"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-[10px] uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">Assigned Hardware Microcontroller ID (ESP32)</label>
                      <input
                        type="text"
                        value={settings.microcontroller_id || ""}
                        disabled={!isSuiteActive}
                        onChange={(e) => handleInputChange("microcontroller_id", e.target.value)}
                        className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder={isSuiteActive ? "e.g. ESP32-SOP-A3B" : "🔒 Subscribe to unlock Hardware Connectors"}
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/5 flex items-center justify-between">
                    <Button
                      type="button"
                      onClick={registerWebPushNotifications}
                      className="btn-ghost-glass text-[11px] rounded-full px-4 py-2"
                    >
                      {pushStatus === "requesting" && "Granting Permissions..."}
                      {pushStatus === "success" && "Chrome Push Registered!"}
                      {pushStatus === "" && "Authorize Chrome Push"}
                    </Button>
                    
                    <div className="flex items-center gap-2">
                      {saveSuccess && (
                        <span className="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                          <CheckCircle2 size={13} /> Saved!
                        </span>
                      )}
                      <Button
                        type="submit"
                        disabled={saving}
                        className="btn-primary-glow rounded-full px-5 py-2 text-xs"
                      >
                        {saving ? "Saving..." : "Save Preferences"}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </GlassCard>
          )}

          {/* TAB 4: AI MODELS & CREDIT ROUTER */}
          {activeTab === "ai_models" && (
            <GlassCard className="p-6 space-y-6">
              <div className="flex items-center gap-2 border-b border-white/5 pb-3">
                <Key size={18} className="text-cyan-300 animate-pulse" />
                <h2 className="font-display text-xl text-white">AI Models & Credit Router</h2>
              </div>
              <p className="text-xs text-[#9AA4B2] leading-relaxed">
                Connect your custom LLM configurations and map specialized sub-models for different application tiers to optimize credit consumption in-bulk.
              </p>

              {loading ? (
                <div className="text-xs text-[#9AA4B2]">Loading parameters...</div>
              ) : (
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  
                  {/* Dynamic global inputs */}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">AI Model Provider</label>
                      <select
                        value={settings.custom_provider || "openai"}
                        onChange={(e) => handleInputChange("custom_provider", e.target.value)}
                        className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-cyan-300"
                      >
                        <option value="openai">OpenAI (GPT API)</option>
                        <option value="anthropic">Anthropic (Claude)</option>
                        <option value="google">Google Gemini</option>
                        <option value="custom_host">Private Webhook</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">Target Model Name</label>
                      <input
                        type="text"
                        value={settings.custom_model || "gpt-4o-mini"}
                        onChange={(e) => handleInputChange("custom_model", e.target.value)}
                        className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-cyan-300"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-semibold">Custom API Key</label>
                      <input
                        type="password"
                        value={settings.custom_api_key || ""}
                        onChange={(e) => handleInputChange("custom_api_key", e.target.value)}
                        className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-cyan-300 font-mono"
                        placeholder="sk-proj-xxxxxxxx"
                      />
                    </div>
                  </div>

                  {/* Credit Optimizer Grouped by Application */}
                  <div className="border-t border-white/5 pt-4 space-y-4">
                    <div className="flex items-center gap-1.5 text-xs text-cyan-300 font-semibold uppercase tracking-wider">
                      <Layers size={14} /> Credit Optimizer (Grouped by Application)
                    </div>
                    
                    <div className="grid sm:grid-cols-3 gap-4">
                      {/* Process SOP Creator (Bulk Import) */}
                      <div className="p-3.5 rounded-2xl bg-white/[0.01] border border-white/5 flex flex-col justify-between h-28">
                        <span className="text-[10px] uppercase tracking-widest text-[#5B6472] font-semibold">1. Bulk Importer Router</span>
                        <select
                          value={settings.creator_app_model || "gpt-4o-mini"}
                          onChange={(e) => handleInputChange("creator_app_model", e.target.value)}
                          className="w-full bg-[#05070D] border border-white/10 rounded-lg p-1.5 text-[10px] text-white focus:outline-none"
                        >
                          <option value="gpt-4o-mini">gpt-4o-mini (Cost-Saving)</option>
                          <option value="gpt-4o">gpt-4o (Standard)</option>
                          <option value="claude-3-haiku">claude-3-haiku (Fast)</option>
                        </select>
                      </div>

                      {/* SOP AI Assistant (Q&A Chat) */}
                      <div className="p-3.5 rounded-2xl bg-white/[0.01] border border-white/5 flex flex-col justify-between h-28">
                        <span className="text-[10px] uppercase tracking-widest text-[#5B6472] font-semibold">2. Q&A Chatbot Router</span>
                        <select
                          value={settings.assistant_app_model || "gpt-4o-mini"}
                          onChange={(e) => handleInputChange("assistant_app_model", e.target.value)}
                          className="w-full bg-[#05070D] border border-white/10 rounded-lg p-1.5 text-[10px] text-white focus:outline-none"
                        >
                          <option value="gpt-4o-mini">gpt-4o-mini (Cost-Saving)</option>
                          <option value="claude-3-5-sonnet">claude-3-5-sonnet (High-Accuracy)</option>
                          <option value="gemini-1.5-flash">gemini-1.5-flash (Standard)</option>
                        </select>
                      </div>

                      {/* Copilot & Hardware Trigger */}
                      <div className="p-3.5 rounded-2xl bg-white/[0.01] border border-white/5 flex flex-col justify-between h-28">
                        <span className="text-[10px] uppercase tracking-widest text-[#5B6472] font-semibold">3. Hardware Copilot Router</span>
                        <select
                          value={settings.copilot_app_model || "gpt-4o-mini"}
                          onChange={(e) => handleInputChange("copilot_app_model", e.target.value)}
                          className="w-full bg-[#05070D] border border-white/10 rounded-lg p-1.5 text-[10px] text-white focus:outline-none"
                        >
                          <option value="gpt-4o-mini">gpt-4o-mini (Standard)</option>
                          <option value="llama-3-8b">llama-3-8b (Ultra Cost-Saving)</option>
                          <option value="gemini-nano">gemini-nano (Local Bus)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/5 flex items-center justify-end gap-2">
                    {saveSuccess && (
                      <span className="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                        <CheckCircle2 size={13} /> Saved!
                      </span>
                    )}
                    <Button
                      type="submit"
                      disabled={saving}
                      className="btn-primary-glow rounded-full px-5 py-2 text-xs"
                    >
                      {saving ? "Saving..." : "Save AI Routing"}
                    </Button>
                  </div>
                </form>
              )}
            </GlassCard>
          )}

        </div>

      </div>
    </div>
  );
}
